import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchGetChatUsers } from 'api/chat';
import { ChatViewId, getCurrentView } from 'hooks/useCurrentChatView';
import { dispatch, store } from 'store';
import { ChatUserModel } from 'types/chat-users/chat-user';
import { ChatUserLabel } from 'types/chat-users/chat-user-label';
import { ChatUsersFilters, ChatUsersFiltersRequest } from 'types/chat-users/filters';
import { HistoryMessage, MessageModel } from 'types/chat/messages/message';
import { ApplicationUser } from 'types/company/applicationUser';
import { fetcher } from 'utils/axios';
import { doesUserMatchAdditionalFilters, doesUserMatchCurrentFilters } from 'utils/chat';
import { migrateCustomView, isFilterGroup } from 'utils/migrate-custom-views';
import { getPathname } from 'utils/helpers';
import { ContactResponse } from 'types/contacts/contact-response';

export const initialFilters: ChatUsersFilters = {
  pageOffset: 1,
  searchQuery: '',
  label: '',
  open: 'all',
  isOnlyUnread: false,
  assigneeFilter: [],
  dontDisturb: false,
  isBot: false,
};

const initialState: {
  selectedChatUser: ChatUserModel | null;
  chatUsers: {
    myChatUsers: ChatUserModel[];
    notMyChatUsers: ChatUserModel[];
    unassignedChatUsers: ChatUserModel[];
    botsChatUsers: ChatUserModel[];
    allChatUsers: ChatUserModel[];
    customChatUsers: ChatUserModel[];
    loading: boolean;
  };
  areUsersRequested: {
    myChatUsers: boolean;
    notMyChatUsers: boolean;
    unassignedChatUsers: boolean;
    botsChatUsers: boolean;
    allChatUsers: boolean;
    customChatUsers: boolean;
  };
  chatUsersFilters: ChatUsersFilters;
  chatUsersSavedMessages: Array<{
    id: string;
    messages: Array<HistoryMessage | MessageModel>;
  }>;
} = {
  selectedChatUser: null,
  chatUsers: {
    myChatUsers: [],
    notMyChatUsers: [],
    unassignedChatUsers: [],
    botsChatUsers: [],
    allChatUsers: [],
    customChatUsers: [],
    loading: false,
  },
  areUsersRequested: {
    myChatUsers: false,
    notMyChatUsers: false,
    unassignedChatUsers: false,
    botsChatUsers: false,
    allChatUsers: false,
    customChatUsers: false,
  },
  chatUsersFilters: initialFilters,
  chatUsersSavedMessages: [],
};

const selectedChatUserSlice = createSlice({
  name: 'selectedChatUser',
  initialState: initialState,
  reducers: {
    setChatUsers(
      state,
      action: PayloadAction<{
        myChatUsers?: ChatUserModel[];
        notMyChatUsers?: ChatUserModel[];
        unassignedChatUsers?: ChatUserModel[];
        botsChatUsers?: ChatUserModel[];
        allChatUsers?: ChatUserModel[];
        customChatUsers?: ChatUserModel[];
      }>,
    ) {
      if (action.payload.notMyChatUsers) state.chatUsers.notMyChatUsers = action.payload.notMyChatUsers;
      if (action.payload.unassignedChatUsers)
        state.chatUsers.unassignedChatUsers = action.payload.unassignedChatUsers;
      if (action.payload.botsChatUsers) state.chatUsers.botsChatUsers = action.payload.botsChatUsers;
      if (action.payload.allChatUsers) state.chatUsers.allChatUsers = action.payload.allChatUsers;
      if (action.payload.myChatUsers) state.chatUsers.myChatUsers = action.payload.myChatUsers;
      if (action.payload.customChatUsers) state.chatUsers.customChatUsers = action.payload.customChatUsers;
    },
    setChatUsersLoading(state, action: PayloadAction<boolean>) {
      state.chatUsers.loading = action.payload;
    },
    storeSetChatUsersRequested(
      state,
      action: PayloadAction<Partial<Record<keyof typeof initialState.areUsersRequested, boolean>>>,
    ) {
      const params = action.payload;
      Object.assign(state.areUsersRequested, params);
    },
    storeUpdateSingleChatUser(state, action: PayloadAction<{ id: number; data: Partial<ChatUserModel> }>) {
      const { id, data } = action.payload;
      if (state.selectedChatUser && state.selectedChatUser.Id === id) {
        Object.assign(state.selectedChatUser, data);
      }
      const states = Object.keys(state.chatUsers).filter((objKey) => objKey !== 'loading') as Array<
        keyof Omit<typeof state.chatUsers, 'loading'>
      >;
      states.forEach((key) => {
        const arr = state.chatUsers[key];
        const idx = arr.findIndex((chatUser) => chatUser.Id === id);
        if (idx !== -1) {
          Object.assign(arr[idx], data);
        }
      });
    },
    storeUpdateSingleChatUserByPhone(
      state,
      action: PayloadAction<{ phone: string; data: Partial<ChatUserModel> }>,
    ) {
      const { phone, data } = action.payload;
      if (state.selectedChatUser && state.selectedChatUser.UserDisplayPhoneNumber === phone) {
        Object.assign(state.selectedChatUser, data);
      }
      const states = Object.keys(state.chatUsers).filter((objKey) => objKey !== 'loading') as Array<
        keyof Omit<typeof state.chatUsers, 'loading'>
      >;
      states.forEach((key) => {
        const arr = state.chatUsers[key];
        const idx = arr.findIndex((chatUser) => chatUser.UserDisplayPhoneNumber === phone);
        if (idx !== -1) {
          Object.assign(arr[idx], data);
        }
      });
    },
    setSelectedChatUser(state, action: PayloadAction<ChatUserModel | null>) {
      state.selectedChatUser = action.payload;
    },
    handleChatUsersFilters(state, action: PayloadAction<Partial<ChatUsersFilters>>) {
      Object.assign(state.chatUsersFilters, action.payload);
    },
    setChatUsersSavedMessages(
      state,
      action: PayloadAction<Array<{ id: string; messages: Array<HistoryMessage | MessageModel> }>>,
    ) {
      const { payload } = action;
      state.chatUsersSavedMessages = payload;
    },
    handleUserSavedMessages(
      state,
      action: PayloadAction<{ id: string; messages: Array<HistoryMessage | MessageModel> }>,
    ) {
      const { id, messages } = action.payload;

      const index = state.chatUsersSavedMessages.findIndex((item) => item.id === id);
      if (index !== -1) {
        state.chatUsersSavedMessages[index].messages = messages;
      } else {
        state.chatUsersSavedMessages.push({ id, messages });
      }
    },
    saveSingleMessageToUser(
      state,
      action: PayloadAction<{ id: string; message: HistoryMessage | MessageModel }>,
    ) {
      const { id, message } = action.payload;
      const index = state.chatUsersSavedMessages.findIndex((item) => item.id === id);
      if (index !== -1) {
        const messagesList = state.chatUsersSavedMessages[index].messages;
        const existingMessageIndex = messagesList.findIndex((msg) => {
          const messageIdMatch = msg.Id && message.Id && msg.Id === message.Id;

          const msgTempId = msg.TempId || msg.tempId;
          const messageTempId = message.TempId || message.tempId;
          const tempIdMatch = msgTempId != null && messageTempId != null && msgTempId === messageTempId;
          return messageIdMatch || tempIdMatch;
        });

        if (existingMessageIndex !== -1) {
          // Update the existing message
          state.chatUsersSavedMessages[index].messages[existingMessageIndex] = {
            ...messagesList[existingMessageIndex],
            ...message,
          };
        } else {
          // Add the new message
          state.chatUsersSavedMessages[index].messages.push(message);
        }
      } else {
        // Create a new entry for the user if it doesn't exist
        state.chatUsersSavedMessages.push({ id, messages: [message] });
      }
    },

    resetChat(state) {
      return initialState;
    },
  },
});

export default selectedChatUserSlice.reducer;
export const {
  setChatUsers,
  setChatUsersLoading,
  storeSetChatUsersRequested,
  storeUpdateSingleChatUser,
  storeUpdateSingleChatUserByPhone,
  setSelectedChatUser,
  setChatUsersSavedMessages,
  handleChatUsersFilters,
  handleUserSavedMessages,
  saveSingleMessageToUser,
  resetChat,
} = selectedChatUserSlice.actions;

//* handlers
export const setChatUsersRequested = (
  params: Partial<Record<keyof typeof initialState.areUsersRequested, boolean>>,
) => {
  dispatch(storeSetChatUsersRequested({ ...params }));
};
export const updateSingleChatUser = (id: number, data: Partial<ChatUserModel>) => {
  dispatch(storeUpdateSingleChatUser({ id, data }));
};
export const updateSingleChatUserByPhone = (phone: string, data: Partial<ChatUserModel>) => {
  dispatch(storeUpdateSingleChatUserByPhone({ phone, data }));
};

//* fetchers
export async function getChatUsers(useLoader: boolean = true, view?: ChatViewId, viewId?: string) {
  if (useLoader) {
    dispatch(setChatUsersLoading(true));
    // נקה את הנתונים הישנים מיד כאשר מתחילים לטעון תצוגה חדשה
    // כדי למנוע הצגת נתונים לא רלוונטיים בזמן הטעינה
    if (view) {
      dispatch(
        setChatUsers({
          [`${view}ChatUsers`]: [],
        }),
      );
    }
  }
  try {
    let params: ChatUsersFiltersRequest;
    const chatUsersFilters = store.getState().chat.chatUsersFilters;
    const currentUser = store.getState().user;
    
    // שליפת פילטרים מותאמים עבור תצוגה מותאמת
    let customFilters: import('types/userConfig/userConfig').FilterGroup[] | undefined;
    let customFiltersLogic: 'or' | 'and' | undefined;
    
    if (view === 'custom' && viewId) {
      const customView = store.getState().userConfig.customViews.find(
        (v) => v.id.toString() === viewId,
      );
      if (customView) {
        // המרת תצוגה ישנה למבנה חדש אם צריך
        const migratedView = migrateCustomView(customView);
        if (migratedView.filters.length > 0 && isFilterGroup(migratedView.filters[0])) {
          customFilters = migratedView.filters as import('types/userConfig/userConfig').FilterGroup[];
          customFiltersLogic = migratedView.filtersLogic || 'and';
        }
      }
    }
    
    // בניית assigneeFilter לפי התצוגה
    let assigneeFilterForView: string[] = [];
    
    if (view === 'my') {
      // "שיחות שלי" - שלוח את ה-UDID של המשתמש הנוכחי
      if (currentUser?.Udid) {
        assigneeFilterForView = ['my']; // Send 'my' as a flag
      }
    } else if (view === 'notMy') {
      // "שיחות של אחרים" - שלוח את כל ה-UDID של המשתמשים האחרים
      assigneeFilterForView = ['notMy']; // Send 'notMy' as a flag
    } else if (view === 'unassigned') {
      // "לא משויכות" - שלוח 'no-assign'
      assigneeFilterForView = ['no-assign'];
    } else if (view === 'bots') {
      // "בוטים" - לא צריך assigneeFilter, אבל צריך is_bot: true
      assigneeFilterForView = [];
    } else {
      // "כל השיחות" או תצוגות אחרות - לא צריך assigneeFilter
      assigneeFilterForView = chatUsersFilters.assigneeFilter || [];
    }
    
    // עבור תצוגות מותאמות עם פילטרים חדשים, נשתמש בסינון בשרת
    // אם אין פילטרים מותאמים, נביא יותר שיחות כדי לסנן אותן בצד הלקוח
    const hasCustomFilters = view === 'custom' && customFilters && customFilters.length > 0;
    const pageSize = view === 'custom' && !hasCustomFilters ? 1000 : 30;
    
    if (chatUsersFilters.searchQuery.length) {
      params = {
        page_number: pageSize,
        page_offset: 0,
        filter_by_open: hasCustomFilters ? chatUsersFilters.open : (view === 'custom' ? 'all' : chatUsersFilters.open),
        order_by: 'last_message_date',
        order_descending: true,
        search_term: hasCustomFilters ? chatUsersFilters.searchQuery : (view === 'custom' ? '' : chatUsersFilters.searchQuery),
        label: hasCustomFilters ? chatUsersFilters.label : (view === 'custom' ? '' : chatUsersFilters.label),
        isOnlyUnread: hasCustomFilters ? chatUsersFilters.isOnlyUnread : (view === 'custom' ? false : chatUsersFilters.isOnlyUnread),
        assigneeFilter: hasCustomFilters ? assigneeFilterForView : (view === 'custom' ? [] : assigneeFilterForView),
        dontDisturb: chatUsersFilters.dontDisturb,
        filters: [],
        customFilters: hasCustomFilters ? customFilters : undefined,
        customFiltersLogic: hasCustomFilters ? customFiltersLogic : undefined,
        is_bot: view === 'bots' ? true : (view === 'custom' ? false : (chatUsersFilters.isBot ?? false)),
      };
    } else {
      params = {
        page_number: view === 'custom' && !hasCustomFilters ? pageSize : 30 * chatUsersFilters.pageOffset,
        page_offset: 0,
        filter_by_open: hasCustomFilters ? chatUsersFilters.open : (view === 'custom' ? 'all' : chatUsersFilters.open),
        order_by: 'last_message_date',
        order_descending: true,
        search_term: hasCustomFilters ? chatUsersFilters.searchQuery : (view === 'custom' ? '' : chatUsersFilters.searchQuery),
        label: hasCustomFilters ? chatUsersFilters.label : (view === 'custom' ? '' : chatUsersFilters.label),
        isOnlyUnread: hasCustomFilters ? chatUsersFilters.isOnlyUnread : (view === 'custom' ? false : chatUsersFilters.isOnlyUnread),
        assigneeFilter: hasCustomFilters ? assigneeFilterForView : (view === 'custom' ? [] : assigneeFilterForView),
        dontDisturb: chatUsersFilters.dontDisturb,
        filters: [],
        customFilters: hasCustomFilters ? customFilters : undefined,
        customFiltersLogic: hasCustomFilters ? customFiltersLogic : undefined,
        is_bot: view === 'bots' ? true : (view === 'custom' ? false : (chatUsersFilters.isBot ?? false)),
      };
    }

    console.log('params getChatUsers', params);
    const data = await fetchGetChatUsers(params);
    console.log('data getChatUsers', data);
    if (data) {
      if (view === 'all') {
        dispatch(
          setChatUsers({
            allChatUsers: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
          }),
        );
        setChatUsersRequested({
          allChatUsers: true,
        });
      } else if (view === 'my') {
        // השרת כבר סינן לפי assigneeId eq <currentUser.Udid> + כל הפילטרים הנוספים
        dispatch(
          setChatUsers({
            myChatUsers: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
          }),
        );
        setChatUsersRequested({
          myChatUsers: true,
        });
      } else if (view === 'notMy') {
        // השרת כבר סינן לפי assigneeId ne <currentUser.Udid> AND assigneeId isnotnull + כל הפילטרים הנוספים
        dispatch(
          setChatUsers({
            notMyChatUsers: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
          }),
        );
        setChatUsersRequested({
          notMyChatUsers: true,
        });
      } else if (view === 'unassigned') {
        // השרת כבר סינן לפי assigneeId isnull + כל הפילטרים הנוספים
        dispatch(
          setChatUsers({
            unassignedChatUsers: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
          }),
        );
        setChatUsersRequested({
          unassignedChatUsers: true,
        });
      } else if (view === 'bots') {
        // השרת כבר סינן לפי activeBot isnotnull + כל הפילטרים הנוספים
        dispatch(
          setChatUsers({
            botsChatUsers: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
          }),
        );
        setChatUsersRequested({
          botsChatUsers: true,
        });
      } else if (view === 'custom') {
        // אם יש פילטרים מותאמים חדשים, השרת כבר סינן הכל
        // אם אין, נסנן בצד הלקוח (למבנה ישן)
        if (hasCustomFilters) {
          // השרת כבר סינן הכל לפי הפילטרים המותאמים
          dispatch(
            setChatUsers({
              customChatUsers: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
            }),
          );
        } else {
          // סינון שיחות לפי הפילטרים המותאמים והפילטרים הרגילים (מבנה ישן)
          const allChats = data?.allChatUsers?.chatUsers || [];
          const chatUsersFilters = store.getState().chat.chatUsersFilters;

          console.log('chatUsersFilters custom', chatUsersFilters);
          console.log('allChats custom', allChats);
          // החל את הפילטרים המותאמים והפילטרים הרגילים על כל השיחות
          const filteredChats = allChats.filter((chat) => 
            // doesUserMatchAdditionalFilters(chatUsersFilters.filters || [], chat) &&
            doesUserMatchCurrentFilters(chat, 'customChatUsers')
          );

          console.log('filteredChats custom', filteredChats);

          dispatch(
            setChatUsers({
              customChatUsers: filteredChats,
            }),
          );
        }
        setChatUsersRequested({
          customChatUsers: true,
        });
      } else {
        dispatch(
          setChatUsers({
            [`${view}ChatUsers`]: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
          }),
        );
        setChatUsersRequested({
          [`${view}ChatUsers`]: true,
        });
      }

      const selectedChatUser = store.getState().chat.selectedChatUser;
      if (selectedChatUser) {
        const updatedUser = data?.allChatUsers.chatUsers.find(
          (user: ChatUserModel) => user.Id === selectedChatUser.Id,
        );
        if (updatedUser) {
          dispatch(setSelectedChatUser(updatedUser));
        }
      }

      return {
        allChatUsers: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
        myChatUsers: data?.myChatUsers?.chatUsers ? [...data.myChatUsers.chatUsers] : [],
      };
    }
  } catch (err) {
    console.warn('getChatUsers error: ', err);
  } finally {
    if (useLoader) dispatch(setChatUsersLoading(false));
  }
}
export async function setNewUserName(phone: string, newName: string) {
  try {
    const formData = {
      phone,
      newName,
    };

    const data = await fetcher(
      'post',
      ['/api/services/UpdateChatUserName', { data: formData }],
      'history',
      // true,
    );

    if (data?.result) {
      updateSingleChatUserByPhone(phone, { FullName: newName });
      return true;
    } else {
      console.log('error editing name');
      return false;
    }
  } catch (error) {
    console.warn('setNewUserName error: ', error);
    return false;
  }
}
export async function handleSetUserLabel(userId: string, option: ChatUserLabel) {
  try {
    await fetcher(
      'post',
      ['/api/services/SetChatUserLabel', { data: { id: userId, labelName: JSON.stringify(option) } }],
      'history',
    );
  } catch (error) {
    console.log('SetChatUserLabel error: ', error);
  }
}
export async function handleSetUserConversationOpenStatus(userId: string, state: boolean) {
  try {
    // קבלת sessionId מהסטייט
    const storeState = store.getState();
    
    // חיפוש ה-ChatUser בסטייט לפי userId
    let sessionId: string | null | undefined = null;
    
    // נסה למצוא ב-selectedChatUser
    if (storeState.chat.selectedChatUser?.UserDisplayPhoneNumber === userId) {
      sessionId = storeState.chat.selectedChatUser.SessionId;
    }
    
    // אם לא נמצא, נסה לחפש בכל הרשימות
    if (!sessionId) {
      const allChatUsers = [
        ...storeState.chat.chatUsers.myChatUsers,
        ...storeState.chat.chatUsers.notMyChatUsers,
        ...storeState.chat.chatUsers.unassignedChatUsers,
        ...storeState.chat.chatUsers.botsChatUsers,
        ...storeState.chat.chatUsers.allChatUsers,
        ...storeState.chat.chatUsers.customChatUsers,
      ];
      
      const chatUser = allChatUsers.find(user => user.UserDisplayPhoneNumber === userId);
      sessionId = chatUser?.SessionId;
    }
    
    // אם לא נמצא sessionId בסטייט, נצטרך לעשות קריאת API
    if (!sessionId) {
      const requestBody = {
        filters: [
          {
            field: 'phone',
            operator: 'equals',
            value: userId,
          },
        ],
        page: 1,
        pageSize: 1,
      };

      const contacts = await fetcher('post', ['/api/services/getContacts', { data: requestBody }], 'servernode') as ContactResponse[] | null;
      
      if (!contacts || !contacts.length || !contacts[0].lastSession?.id) {
        console.error('Could not find sessionId for user:', userId);
        return;
      }

      sessionId = contacts[0].lastSession.id;
    }

    const status = state ? 'open' : 'closed';

    // עדכון הסטטוס דרך ה-API החדש
    await fetcher('put', [`/api/sessions/${sessionId}`, { data: { status } }], 'newapi');
  } catch (error) {
    console.error('handleSetUserConversationOpenStatus error:', error);
  }
}
export async function handleSetUserAssignee(userId: string, assignee: ApplicationUser | null) {
  try {
    // קבלת sessionId מהסטייט
    const storeState = store.getState();
    
    // חיפוש ה-ChatUser בסטייט לפי userId
    let sessionId: string | null | undefined = null;
    
    // נסה למצוא ב-selectedChatUser
    if (storeState.chat.selectedChatUser?.UserDisplayPhoneNumber === userId) {
      sessionId = storeState.chat.selectedChatUser.SessionId;
    }
    
    // אם לא נמצא, נסה לחפש בכל הרשימות
    if (!sessionId) {
      const allChatUsers = [
        ...storeState.chat.chatUsers.myChatUsers,
        ...storeState.chat.chatUsers.notMyChatUsers,
        ...storeState.chat.chatUsers.unassignedChatUsers,
        ...storeState.chat.chatUsers.botsChatUsers,
        ...storeState.chat.chatUsers.allChatUsers,
        ...storeState.chat.chatUsers.customChatUsers,
      ];
      
      const chatUser = allChatUsers.find(user => user.UserDisplayPhoneNumber === userId);
      sessionId = chatUser?.SessionId;
    }
    
    // אם לא נמצא sessionId בסטייט, נצטרך לעשות קריאת API
    if (!sessionId) {
      const requestBody = {
        filters: [
          {
            field: 'phone',
            operator: 'equals',
            value: userId,
          },
        ],
        page: 1,
        pageSize: 1,
      };

      const contacts = await fetcher('post', ['/api/services/getContacts', { data: requestBody }], 'servernode') as ContactResponse[] | null;
      
      if (!contacts || !contacts.length || !contacts[0].lastSession?.id) {
        console.error('Could not find sessionId for user:', userId);
        return;
      }

      sessionId = contacts[0].lastSession.id;
    }

    const managerId = assignee ? assignee.Udid : null;

    // עדכון המנהל דרך ה-API החדש
    // השרת ישלח סוקט לעדכון הסטייט, אז אין צורך לעדכן אותו כאן
    await fetcher('put', [`/api/sessions/${sessionId}`, { data: { managerId } }], 'newapi');
  } catch (error) {
    console.error('handleSetUserAssignee error:', error);
  }
}
